import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RfqTenderSubmissionGuidlinesComponent } from '../rfq-tender-submission-guidlines/rfq-tender-submission-guidlines.component';
import { RfqContractualTermsComponent } from '../rfq-contractual-terms/rfq-contractual-terms.component';
import { RfqKeyDatesComponent } from '../rfq-key-dates/rfq-key-dates.component';
import { RfqWorkPackagesComponent } from '../rfq-work-packages/rfq-work-packages.component';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { ActivatedRoute } from '@angular/router';
import { BriefSectionComponent } from '@/app/components/projects/project-development/tendering/project-expression-of-interest/brief-section/brief-section.component';
import { RfqCompaniesSelectionComponent } from '../../../../shared/developer/rfq-companies-selection/rfq-companies-selection.component';
import { RequestForQuotationService } from '@/app/services/tendering-services/rfq/rfq.service';
@Component({
  selector: 'app-rfq-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    RfqCompaniesSelectionComponent,
    BriefSectionComponent,
    RfqWorkPackagesComponent,
    RfqKeyDatesComponent,
    RfqContractualTermsComponent,
    RfqTenderSubmissionGuidlinesComponent,
  ],
  templateUrl: './rfq-wrapper.component.html',
  styleUrl: './rfq-wrapper.component.css',
})
export class RfqWrapperComponent implements OnInit {
  @Output() closeModalEvent = new EventEmitter<boolean>();
  @Input() isEdit: boolean;

  private readonly totalSteps = 6;

  currentRfqStep = 1;
  // currently saved ITT data with step no
  ITTDeveloperData: any = {};
  projectId: number;
  projectName: string;
  rfqId: number;

  constructor(
    private tenderingService: TenderingService,
    private requestForQuotationService: RequestForQuotationService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    /**
     * NOTE:
     * We will get project details here by using the project id from the url
     *  and assign it to this.project
     * This way we will have project object no matter what
     */

    /**
     * Getting itt saved developer data in itt wrapper component then when
     *  if the data changes inside the components of itt wrapper then emit
     *  event and get new data and update it in the parent hence we will
     *  have updated itt data in the parent and as it is reactive, we will
     *  always have updated data in all components
     */

    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    this.requestForQuotationService.rfqId$.subscribe((rfqId) => {
      this.rfqId = rfqId;
    });

    /**
     * If we are editing, get the record and current step separately
     *  from backend and send data to child components and show respective wizard
     * When a child component will update ITTDeveloperData object,
     *  we will emit and get changes in this `itt-wrapper` component and
     *  will update the ITTDeveloperData object, which will send
     *  latest data to all child components as it is reactive
     */
    // IMP: Currently we are only sending editId for ITT/RFP.
    if (this.isEdit && this.rfqId) {
      this.getITTDeveloperDataWithStep();
    }

    // IMP: On create, we need to set rfqId to null
    if (!this.isEdit) {
      this.requestForQuotationService.setRFQId(null);
    }
  }

  getITTDeveloperDataWithStep() {
    return this.requestForQuotationService
      .rfqDeveloperDataWithStep(this.projectId, this.rfqId)
      .subscribe({
        next: (response: any) => {
          if (response?.meta?.success) {
            this.handleRfqDeveloperData(response?.data);
          }
        },
        error: (error) => {
          console.error('Error fetching data:', error);
        },
      });
  }

  // to update the wizard fields and show proper wizard when developer edits an itt
  handleRfqDeveloperData(developerData) {
    this.currentRfqStep = developerData.ittStep;

    // Step 2 data
    this.ITTDeveloperData['title'] = developerData?.ittData?.title;
    this.ITTDeveloperData['work_packages_des'] =
      developerData?.ittData?.work_packages_des;
    this.ITTDeveloperData['p_w_p'] =
      developerData?.ittData?.selected_work_packages;

    // Step 3 data
    this.ITTDeveloperData['tendering_type'] =
      developerData?.ittData?.tendering_type;
    this.ITTDeveloperData['receiverOrganizations'] =
      developerData?.ittData?.selected_organizations;

    // Step 4 data
    const datetime = developerData?.ittData?.submission_deadline;
    const dateObj = new Date(datetime);

    // Extract date and time
    const date = dateObj.toISOString().split('T')[0]; // "2025-01-25"
    const time = dateObj.toTimeString().split(' ')[0]; // "12:00:00"

    this.ITTDeveloperData['submission_address'] =
      developerData?.ittData?.submission_address;
    this.ITTDeveloperData['submission_deadline_date'] = date;
    this.ITTDeveloperData['submission_deadline_time'] = time;

    // Step 5 data
    this.ITTDeveloperData['contract_type_details'] =
      developerData?.ittData?.contract_type_details;
    this.ITTDeveloperData['contract_type_attachments'] =
      developerData?.ittData?.contractual_terms_attachments;

    // Step 6 data
    this.ITTDeveloperData['issue_date'] = developerData?.ittData?.issue_date;
    this.ITTDeveloperData['clarification_deadline'] =
      developerData?.ittData?.clarification_deadline;
    this.ITTDeveloperData['contract_award_notification'] =
      developerData?.ittData?.contract_award_notification;
    this.ITTDeveloperData['contract_start_date'] =
      developerData?.ittData?.contract_start_date;
    this.ITTDeveloperData['project_completion_date'] =
      developerData?.ittData?.project_completion_date;

    // Step 7 data
    const technicalEvaluation =
      developerData?.ittData?.technical_evaluation_criteria;
    this.ITTDeveloperData['technical_evaluation'] = {};
    this.ITTDeveloperData['technical_evaluation']['scope_and_objectives'] =
      technicalEvaluation?.[0]?.weight;
    this.ITTDeveloperData['technical_evaluation']['methodology_and_approach'] =
      technicalEvaluation?.[1]?.weight;
    this.ITTDeveloperData['technical_evaluation']['team_and_resource'] =
      technicalEvaluation?.[2]?.weight;
    this.ITTDeveloperData['technical_evaluation']['quality_and_risk'] =
      technicalEvaluation?.[3]?.weight;
    this.ITTDeveloperData['technical_evaluation']['value_and_sustainability'] =
      technicalEvaluation?.[4]?.weight;

    // Step 8 data
    const commercialEvaluation =
      developerData?.ittData?.commercial_evaluation_criteria;
    this.ITTDeveloperData['commercial_evaluation'] = {};
    this.ITTDeveloperData['commercial_evaluation']['price_competitiveness'] =
      commercialEvaluation?.[0]?.weight;
    this.ITTDeveloperData['commercial_evaluation']['payment_terms'] =
      commercialEvaluation?.[1]?.weight;
    this.ITTDeveloperData['commercial_evaluation']['financial_stability'] =
      commercialEvaluation?.[2]?.weight;
    this.ITTDeveloperData['commercial_evaluation'][
      'commercial_risk_allocation'
    ] = commercialEvaluation?.[3]?.weight;
  }

  nextStep() {
    if (this.currentRfqStep < this.totalSteps) {
      this.currentRfqStep++;
    }
  }

  previousStep() {
    if (this.currentRfqStep > 1) {
      this.currentRfqStep--;
    }
  }

  handleCloseModal(closeModalValue: boolean) {
    if (closeModalValue) {
      this.closeModalEvent.emit(closeModalValue); // Emit closeModal value to the parent
    }
  }

  // Step 2 wizard updation
  handleWorkPackagesData(workPackagesEmittedData: any) {
    console.log(
      'workPackagesEmittedData?.ittRecord?.id',
      workPackagesEmittedData?.ittRecord?.id
    );

    this.ITTDeveloperData['title'] = workPackagesEmittedData?.ittRecord?.title;
    this.ITTDeveloperData['work_packages_des'] =
      workPackagesEmittedData?.ittRecord?.work_packages_des;
    this.ITTDeveloperData['p_w_p'] = workPackagesEmittedData?.ittWPRecord;
    this.requestForQuotationService.setRFQId(
      workPackagesEmittedData?.ittRecord?.id
    );
  }

  // Step 3 wizard updation
  handleCompaniesSelectionData(companiesSelectionEmittedData: any) {
    this.ITTDeveloperData['tendering_type'] =
      companiesSelectionEmittedData?.ittRecord?.tendering_type;
    this.ITTDeveloperData['receiverOrganizations'] =
      companiesSelectionEmittedData?.ittReceiverOrganizationRecord;
    this.requestForQuotationService.setRFQId(
      companiesSelectionEmittedData?.ittRecord?.id
    );
  }

  // Step 4 wizard updation
  handleTenderSubmissionData(tenderSubmissionEmittedData: any) {
    const datetime = tenderSubmissionEmittedData?.submission_deadline;
    const dateObj = new Date(datetime);

    // Extract date and time
    const date = dateObj.toISOString().split('T')[0]; // "2025-01-25"
    const time = dateObj.toTimeString().split(' ')[0]; // "12:00:00"

    this.ITTDeveloperData['submission_address'] =
      tenderSubmissionEmittedData?.submission_address;
    this.ITTDeveloperData['submission_deadline_date'] = date;
    // TODO: time
    this.ITTDeveloperData['submission_deadline_time'] = time;
    this.requestForQuotationService.setRFQId(tenderSubmissionEmittedData?.id);
  }

  // Step 5 wizard updation
  handlecontractualTermsData(contractualTermsEmittedData: any) {
    this.ITTDeveloperData['contract_type_details'] =
      contractualTermsEmittedData?.contract_type_details;
    this.ITTDeveloperData['contract_type_attachments'] =
      contractualTermsEmittedData?.contractual_terms_attachments;
    this.requestForQuotationService.setRFQId(contractualTermsEmittedData?.id);
  }

  // Step 6 wizard updation
  handlekeyDatesData(keyDatesEmittedData: any) {
    this.ITTDeveloperData['issue_date'] = keyDatesEmittedData?.issue_date;
    this.ITTDeveloperData['clarification_deadline'] =
      keyDatesEmittedData?.clarification_deadline;
    this.ITTDeveloperData['contract_award_notification'] =
      keyDatesEmittedData?.contract_award_notification;
    this.ITTDeveloperData['contract_start_date'] =
      keyDatesEmittedData?.contract_start_date;
    this.ITTDeveloperData['project_completion_date'] =
      keyDatesEmittedData?.project_completion_date;

    this.requestForQuotationService.setRFQId(keyDatesEmittedData?.id);
    this.handleCloseModal(true);
  }

  // Step 7 wizard updation
  handleTechnicalEvaluationData(technicalEvaluationDataEmittedData: any) {
    const technicalEvaluation =
      technicalEvaluationDataEmittedData?.technical_evaluation_criteria;

    this.ITTDeveloperData['technical_evaluation'] = {};
    this.ITTDeveloperData['technical_evaluation']['scope_and_objectives'] =
      technicalEvaluation?.[0]?.weight;
    this.ITTDeveloperData['technical_evaluation']['methodology_and_approach'] =
      technicalEvaluation?.[1]?.weight;
    this.ITTDeveloperData['technical_evaluation']['team_and_resource'] =
      technicalEvaluation?.[2]?.weight;
    this.ITTDeveloperData['technical_evaluation']['quality_and_risk'] =
      technicalEvaluation?.[3]?.weight;
    this.ITTDeveloperData['technical_evaluation']['value_and_sustainability'] =
      technicalEvaluation?.[4]?.weight;

    this.requestForQuotationService.setRFQId(
      technicalEvaluationDataEmittedData?.id
    );
  }
}
